import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/material';

import CustomPopupText from '../shared/CustomPopupText';
import CustomTextField from '../shared/CustomTextField';
import CustomRadioGroup from '../shared/CustomRadioGroup';
import CustomUploadField from '../shared/CustomUploadField';
import Trash from '../../assets/Trash';
import { INITIAL_RESOURCE_CARD_DATA, AGENDA_TYPE_OPTIONS } from './constants';

const ResourceCard = ({ data: propData, setData, onDelete, expand = false }) => {
  const [expanded, setExpanded] = React.useState(expand);
  const [localData, setLocalData] = React.useState(propData || INITIAL_RESOURCE_CARD_DATA);

  const isEditable = Boolean(setData);

  React.useEffect(() => {
    setLocalData(propData || INITIAL_RESOURCE_CARD_DATA);
  }, [propData]);

  // const updateData = (updatedData) => {
  //   setLocalData(updatedData);
  //   if (isEditable) {
  //     setData(updatedData);
  //   }
  // };

  const handleAccordionChange = (_, newExpanded) => {
    setExpanded(newExpanded);
  };

  const handleData = ({ target: { name, value } }) => {
    if (!isEditable) return;

    setLocalData((prev) => {
      let updatedData = { ...prev, [name]: value };

      if (name === 'type') {
        updatedData =
          value === 'file'
            ? { ...updatedData, url: '' }
            : { ...updatedData, file: null, filePreview: '', fileName: '' };
      }

      setData(updatedData);
      return updatedData;
    });
  };

  const handleFileUpload = (e) => {
    if (!isEditable) return;

    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const isImage = uploadedFile.type.startsWith('image/');

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        // Use functional update to avoid stale closure
        setLocalData((prev) => {
          const updated = {
            ...prev,
            file: uploadedFile,
            fileName: uploadedFile.name,
            filePreview: ev.target.result,
          };
          if (isEditable) {
            setData(updated);
          }
          return updated;
        });
      };
      reader.readAsDataURL(uploadedFile);
    } else {
      // For non-image files (PDFs, documents, etc.), just set the file and fileName without preview
      // Use functional update to avoid stale closure
      setLocalData((prev) => {
        const updated = {
          ...prev,
          file: uploadedFile,
          fileName: uploadedFile.name,
          filePreview: '',
        };
        if (isEditable) {
          setData(updated);
        }
        return updated;
      });
    }

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleDeleteFile = () => {
    if (!isEditable) return;

    // Use functional update to avoid stale closure
    setLocalData((prev) => {
      const updated = {
        ...prev,
        file: null,
        filePreview: '',
        fileName: '',
      };
      if (isEditable) {
        setData(updated);
      }
      return updated;
    });
  };

  const resourceType = localData?.type || 'file';

  return (
    <div style={{ width: '100%' }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <StyledTypography component="span">{localData?.name || 'Add Resource'}</StyledTypography>

            {onDelete && (
              <Trash
                style={{ width: '12px', height: '12px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container direction="column">
            <Grid item sx={{ mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item mr={3}>
                  <CustomPopupText text="Type" required />
                </Grid>
                <Grid item>
                  <CustomRadioGroup
                    name="type"
                    value={resourceType}
                    options={AGENDA_TYPE_OPTIONS}
                    handleChange={handleData}
                    disabled={!isEditable}
                    row={false}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item sx={{ mb: 2 }}>
              <CustomPopupText text="Resource Name" required />
              <CustomTextField
                name="name"
                value={localData?.name || ''}
                handleChange={handleData}
                width="100%"
                disabled={!isEditable}
              />
            </Grid>

            {resourceType === 'link' ? (
              <Grid item sx={{ mb: 2 }}>
                <CustomPopupText text="Add Link" required />
                <CustomTextField
                  name="url"
                  value={localData?.url || ''}
                  handleChange={handleData}
                  width="100%"
                  disabled={!isEditable}
                />
              </Grid>
            ) : (
              <Grid item sx={{ mb: 2 }}>
                <CustomPopupText text="Upload File" required />
                <CustomUploadField
                  name={`file-${localData?.id || 'new'}`}
                  value={localData?.filePreview || ''}
                  handleChange={handleFileUpload}
                  handleDelete={handleDeleteFile}
                  fileName={localData?.fileName || ''}
                  customStyles={{ height: '44px', width: '100%' }}
                  wordWrapWidth="180px"
                  accept="*/*"
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowDropUpIcon sx={{ fontSize: '1.5rem' }} />} {...props} />
))(({ theme }) => ({
  height: '56px',
  padding: '16px',
  justifyContent: 'space-between',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(180deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const StyledTypography = styled(Typography)(() => ({
  fontFamily: 'Montserrat',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '19.6px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: '350px',
  whiteSpace: 'nowrap',
  display: 'block',
}));

export default ResourceCard;
